import type {
  ViewerInteractionMenuAction,
  ViewerInteractionMenuPolicyContext,
  ViewerInteractionMenuUser,
  ViewerInteractionMenuVariant,
  ViewerInteractionMenuViewer,
} from './viewerInteractionMenu.types';

interface GetViewerInteractionMenuActionsParams {
  user: ViewerInteractionMenuUser;
  viewer: ViewerInteractionMenuViewer;
  variant?: ViewerInteractionMenuVariant;
  context: ViewerInteractionMenuPolicyContext;
}

const FAN_RANK_ACTIONS = new Set<ViewerInteractionMenuAction>(['sendGiftStore', 'sendGiftOwnedItem', 'sendPost']);

function isMenuHidden({ user, viewer, context }: GetViewerInteractionMenuActionsParams) {
  return !context.isVisible || context.isMe || viewer.isSupport || (user.isRoomMaster && viewer.isRoomMaster);
}

function getModerationFlags({ user, viewer }: Pick<GetViewerInteractionMenuActionsParams, 'user' | 'viewer'>) {
  const canModerate = user.isRoomMaster || user.isManager;
  const viewerIsStaff = !!viewer.isRoomMaster || !!viewer.isManager;
  return {
    canManageManagerRole: user.isRoomMaster && !viewer.isRoomMaster && !viewer.isJapanese,
    canModerateViewer: canModerate && !viewerIsStaff,
  };
}

function getManagerActions(params: GetViewerInteractionMenuActionsParams): ViewerInteractionMenuAction[] {
  const { viewer, context } = params;
  const { canManageManagerRole } = getModerationFlags(params);

  if (!canManageManagerRole || !context.hasPlayInfo) return [];

  return [viewer.isManager ? 'dismissManager' : 'assignManager'];
}

function getModerationActions(params: GetViewerInteractionMenuActionsParams): ViewerInteractionMenuAction[] {
  const { context } = params;
  const { canModerateViewer } = getModerationFlags(params);

  if (!canModerateViewer || !context.hasPlayInfo) return [];

  return [context.isAlreadyMuted ? 'unmuteChat' : 'muteChat', 'permanentBan', 'temporaryBan'];
}

function getGiftActions({ viewer, context }: GetViewerInteractionMenuActionsParams): ViewerInteractionMenuAction[] {
  if (context.isAsp || viewer.isJapanese) return [];

  return ['sendGiftStore', 'sendGiftOwnedItem'];
}

function getSendPostActions({
  user,
  viewer,
  context,
}: GetViewerInteractionMenuActionsParams): ViewerInteractionMenuAction[] {
  if (viewer.isRoomMaster || !context.isPostAllowed || (!user.isRoomMaster && viewer.isJapanese)) return [];

  return ['sendPost'];
}

function getLivePickActions(params: GetViewerInteractionMenuActionsParams): ViewerInteractionMenuAction[] {
  const { canModerateViewer } = getModerationFlags(params);
  return canModerateViewer ? ['livePick'] : [];
}

function getReportActions({ viewer }: GetViewerInteractionMenuActionsParams): ViewerInteractionMenuAction[] {
  return viewer.isRoomMaster ? [] : ['reportViewer'];
}

export function getViewerInteractionMenuActions(
  params: GetViewerInteractionMenuActionsParams,
): ViewerInteractionMenuAction[] {
  const { variant = 'default' } = params;

  if (isMenuHidden(params)) return [];

  const actions = [
    ...getManagerActions(params),
    ...getModerationActions(params),
    ...getGiftActions(params),
    ...getSendPostActions(params),
    ...getLivePickActions(params),
    ...getReportActions(params),
  ];

  return variant === 'fanRank' ? actions.filter((action) => FAN_RANK_ACTIONS.has(action)) : actions;
}
